import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { StepperField } from "../shared/Fields";

type HouseholdDialogProps = {
  open: boolean;
  onClose: () => void;
  adultsCount: number;
  kidsCount: number;
  kidsAgeValues: number[];
  onAdjustHouseholdCount: (
    key: "adults" | "kids",
    delta: number,
    min?: number
  ) => void;
  onAdjustKidsAge: (index: number, delta: number) => void;
  onSave: () => void;
};

const HouseholdDialog = ({
  open,
  onClose,
  adultsCount,
  kidsCount,
  kidsAgeValues,
  onAdjustHouseholdCount,
  onAdjustKidsAge,
  onSave,
}: HouseholdDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    scroll="body"
    PaperProps={{
      sx: {
        borderRadius: "20px",
        px: { xs: 2.5, sm: 3 },
        py: { xs: 2, sm: 2.5 },
        overflow: "visible",
      },
    }}
  >
    <DialogTitle
      sx={{
        p: 0,
        mb: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Household Details
      </Typography>
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 0, overflow: "visible" }}>
      <Stack spacing={3}>
        <Typography sx={{ color: "#4B5563", fontSize: "14px" }}>
          Tell us how many adults and kids, and kids' ages (if any).
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
            Adults
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
            Kids
          </Typography>
          <StepperField
            value={adultsCount}
            onDecrease={() => onAdjustHouseholdCount("adults", -1, 1)}
            onIncrease={() => onAdjustHouseholdCount("adults", 1, 1)}
          />
          <StepperField
            value={kidsCount}
            onDecrease={() => onAdjustHouseholdCount("kids", -1, 0)}
            onIncrease={() => onAdjustHouseholdCount("kids", 1, 0)}
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#111827", mb: 1 }}
          >
            Kids' ages (optional)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(140px, 1fr))",
              gap: 2,
            }}
          >
            {kidsAgeValues.map((age, index) => (
              <StepperField
                key={`kid-age-${index}`}
                value={age}
                onDecrease={() => onAdjustKidsAge(index, -1)}
                onIncrease={() => onAdjustKidsAge(index, 1)}
              />
            ))}
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={onSave}
          fullWidth
          sx={{
            textTransform: "none",
            backgroundColor: "#1C1C1C",
            borderRadius: "12px",
            py: 1.2,
            fontWeight: 600,
            "&:hover": { backgroundColor: "#1C1C1C" },
          }}
        >
          Save Household
        </Button>
      </Stack>
    </DialogContent>
  </Dialog>
);

export default HouseholdDialog;
